import React, { useState, useEffect } from 'react';

import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { firestore } from '../firebase/config';

import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

import { calculateDuration } from '../utils/timeUtils';



function StudentDetails() {

  const [student, setStudent] = useState(null);

  const [loading, setLoading] = useState(true);

  const [attendance, setAttendance] = useState([]);

  const [attendanceLoading, setAttendanceLoading] = useState(true);

  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const { id } = useParams();

  const navigate = useNavigate();

  const location = useLocation();



  useEffect(() => {

    const fetchStudent = async () => {

      try {

        const studentDoc = await getDoc(doc(firestore, 'students', id));

        if (studentDoc.exists()) {

          setStudent({ id: studentDoc.id, ...studentDoc.data() });

        } else {

          console.log('Student not found');

          navigate('/admin');

        }

      } catch (error) {

        console.error('Error fetching student:', error);

        navigate('/admin');

      }

      setLoading(false);

    };

    fetchStudent();

  }, [id, navigate]);



  useEffect(() => {
    const fetchAttendance = async () => {
      if (!student) return;
      setAttendanceLoading(true);

      try {
        // Simplified approach: try the student's document ID first, then UID if different
        const possibleUids = [student.id];
        if (student.uid && student.uid !== student.id) {
          possibleUids.push(student.uid);
        }
        
        console.log('StudentDetails: Student data:', student);
        console.log('StudentDetails: Possible UIDs:', possibleUids);
        
        // Try each possible UID to find attendance records
        let records = [];
        for (const uid of possibleUids) {
          console.log('StudentDetails: Trying UID:', uid);
          const q = query(collection(firestore, 'studentAttendance'), where('uid', '==', uid));
          const querySnapshot = await getDocs(q);
          const foundRecords = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('StudentDetails: Found records for UID', uid, ':', foundRecords.length);
          
          if (foundRecords.length > 0) {
            records = foundRecords;
            console.log('StudentDetails: Using UID:', uid, 'with', records.length, 'records');
            break;
          }
        }
        
        // If no records found with UIDs, try searching by student name as fallback
        if (records.length === 0) {
          console.log('StudentDetails: No records found with UIDs, trying to search by name:', student.fullName);
          
          // Get all attendance records and filter by student name
          const allAttendanceQuery = query(collection(firestore, 'studentAttendance'));
          const allAttendanceSnapshot = await getDocs(allAttendanceQuery);
          const allRecords = allAttendanceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          // Try to find records that might belong to this student
          const potentialRecords = allRecords.filter(record => {
            return record.fullName === student.fullName || 
                   record.studentName === student.fullName ||
                   record.name === student.fullName;
          });
          
          console.log('StudentDetails: Potential records by name:', potentialRecords.length);
          if (potentialRecords.length > 0) {
            records = potentialRecords;
            console.log('StudentDetails: Using records found by name matching');
          }
        }
        
        console.log('StudentDetails: Final attendance records:', records.length, records);
        
        // Store debug information for display
        setDebugInfo({
          studentData: student,
          possibleUids: possibleUids,
          foundRecords: records,
          allRecordsCount: records.length
        });
        
        setAttendance(records);

      } catch (err) {
        console.error('Error fetching attendance:', err);
        setAttendance([]);
      }

      setAttendanceLoading(false);
    };

    fetchAttendance();
  }, [student]);


  // Attendance summary

  const totalPresent = attendance.filter(a => a.checkIn && a.checkOut).length;

  const totalAbsent = attendance.filter(a => !a.checkIn).length;

  const totalCheckedIn = attendance.filter(a => a.checkIn && !a.checkOut).length;

  const allDays = attendance.length;



  if (loading) {

    return (

      <div className="min-h-screen bg-gray-50 flex items-center justify-center">

        <div className="text-xl text-gray-600">Loading...</div>

      </div>

    );

  }



  if (!student) {

    return (

      <div className="min-h-screen bg-gray-50 flex items-center justify-center">

        <div className="text-xl text-gray-600">Student not found</div>

      </div>

    );

  }



  return (

    <div className="min-h-screen bg-gray-50 py-8">

      <div className="max-w-4xl mx-auto px-4">

        {/* Back Button */}

        <div className="mb-6">

          <div className="uiverse-box">

          <button 

            onClick={() => {

              const params = new URLSearchParams(location.search);

              if (params.get('tab') === 'students') {

                navigate('/admin?tab=students');

              } else {

                navigate('/admin');

              }

            }}

            className="flex items-center px-0 py-0 bg-transparent text-black rounded-full font-bold text-base gap-2 focus:outline-none border-none shadow-none"

          >

            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">

              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />

            </svg>

            Back to Dashboard

          </button>

          </div>

        </div>



        {/* Student Profile Header */}

        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">

          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6">

            <div className="flex items-center">

              <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center">

                <span className="text-3xl font-bold text-white">

                  {student.fullName ? student.fullName.charAt(0).toUpperCase() : 'S'}

                </span>

              </div>

              <div className="ml-6">

                <h1 className="text-3xl font-bold text-white">{student.fullName}</h1>

                <p className="text-blue-100 text-lg">{student.courseName}</p>

                <p className="text-blue-100">Student ID: {student.id}</p>

              </div>

            </div>

          </div>

        </div>



        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* Personal Information */}

          <div className="bg-white rounded-xl shadow-lg p-6">

            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">

              <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />

              </svg>

              Personal Information

            </h2>

            

            <div className="space-y-4">

              <div className="flex items-center p-3 bg-gray-50 rounded-lg">

                <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />

                </svg>

                <div>

                  <p className="text-sm text-gray-600">Full Name</p>

                  <p className="font-medium">{student.fullName}</p>

                </div>

              </div>



              <div className="flex items-center p-3 bg-gray-50 rounded-lg">

                <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />

                </svg>

                <div>

                  <p className="text-sm text-gray-600">Father's Name</p>

                  <p className="font-medium">{student.fatherName}</p>

                </div>

              </div>



              <div className="flex items-start p-3 bg-gray-50 rounded-lg">

                <svg className="w-5 h-5 text-gray-500 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />

                </svg>

                <div>

                  <p className="text-sm text-gray-600">Address</p>

                  <p className="font-medium">{student.address}</p>

                </div>

              </div>



              <div className="flex items-center p-3 bg-gray-50 rounded-lg">

                <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V4a2 2 0 114 0v2m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />

                </svg>

                <div>

                  <p className="text-sm text-gray-600">CNIC Number</p>

                  <p className="font-medium">{student.cnicNo}</p>

                </div>

              </div>



              <div className="flex items-center p-3 bg-gray-50 rounded-lg">

                <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />

                </svg>

                <div>

                  <p className="text-sm text-gray-600">Contact Number</p>

                  <p className="font-medium">{student.contactNumber}</p>

                </div>

              </div>

              {student.secondaryContactNumber && (

                <div className="flex items-center p-3 bg-gray-50 rounded-lg">

                  <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />

                  </svg>

                  <div>

                    <p className="text-sm text-gray-600">Secondary/Father Contact Number</p>

                    <p className="font-medium">{student.secondaryContactNumber}</p>

                  </div>

                </div>

              )}

            </div>

          </div>



          {/* Course & Fee Information */}

          <div className="bg-white rounded-xl shadow-lg p-6">

            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">

              <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />

              </svg>

              Course & Fee Information

            </h2>

            

            <div className="space-y-4">

              <div className="flex items-center p-3 bg-gray-50 rounded-lg">

                <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />

                </svg>

                <div>

                  <p className="text-sm text-gray-600">Course Name</p>

                  <p className="font-medium">{student.courseName}</p>

                </div>

              </div>



              <div className="flex items-center p-3 bg-gray-50 rounded-lg">

                <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />

                </svg>

                <div>

                  <p className="text-sm text-gray-600">Admission Date</p>

                  <p className="font-medium">{student.admissionDate}</p>

                </div>

              </div>



              {/* Fee Details */}

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">

                <h3 className="text-lg font-semibold text-gray-800 mb-4">Fee Details</h3>

                <div className="space-y-3">

                  <div className="flex justify-between items-center">

                    <span className="text-gray-600">Total Fee:</span>

                    <span className="font-semibold text-gray-800">PKR {student.totalFee}</span>

                  </div>

                  <div className="flex justify-between items-center">

                    <span className="text-gray-600">Submitted Fee:</span>

                    <span className="font-semibold text-green-600">PKR {student.submittedFee}</span>

                  </div>

                  <div className="flex justify-between items-center border-t pt-3">

                    <span className="text-gray-600 font-medium">Pending Fee:</span>

                    <span className={`font-bold ${parseFloat(student.pendingFee) === 0 ? 'text-green-600' : 'text-red-600'}`}>

                      PKR {student.pendingFee}

                    </span>

                  </div>

                </div>

              </div>



              {student.pendingFeeSubmissionDate && (

                <div className="flex items-center p-3 bg-gray-50 rounded-lg">

                  <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />

                  </svg>

                  <div>

                    <p className="text-sm text-gray-600">Pending Fee Submission Date</p>

                    <p className="font-medium">{student.pendingFeeSubmissionDate}</p>

                  </div>

                </div>

              )}

            </div>

          </div>

        </div>



        {/* Attendance History */}

        <div className="bg-white rounded-xl shadow-lg p-6 mt-8">

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
            <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">

              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />

            </svg>

            Attendance History

          </h2>
          </div>
          
          {/* Debug Information Panel */}
          {showDebugInfo && debugInfo && (
            <div className="bg-gray-100 rounded-lg p-4 mb-6 text-sm">
              <h3 className="font-bold text-gray-800 mb-2">🔧 Debug Information:</h3>
              <div className="space-y-2">
                <p><strong>Student ID (from URL):</strong> {id}</p>
                <p><strong>Student UID (from document):</strong> {debugInfo.studentData?.uid || 'None'}</p>
                <p><strong>Student Contact Number:</strong> {debugInfo.studentData?.contactNumber}</p>
                <p><strong>All Possible UIDs Tried:</strong> {debugInfo.possibleUids?.join(', ') || 'None'}</p>
                <p><strong>Records Found:</strong> {debugInfo.foundRecords?.length || 0}</p>
                <p><strong>Total Records in DB:</strong> {debugInfo.allRecordsCount}</p>
              </div>
              <div className="mt-3 text-xs text-gray-600">
                <p>💡 <strong>Tip:</strong> Open browser console (F12) for detailed logs</p>
              </div>
            </div>
          )}
          

          {attendanceLoading ? (

            <div className="text-center py-8 text-gray-500">Loading attendance...</div>

          ) : (

            <>

              <div className="flex gap-6 mb-4">

                <span className="text-green-600 font-semibold">Total Present: {totalPresent}</span>

                <span className="text-red-600 font-semibold">Total Absent: {totalAbsent}</span>

                <span className="text-yellow-600 font-semibold">Checked In: {totalCheckedIn}</span>

                <span className="text-gray-700 font-semibold">All Days: {allDays}</span>

              </div>

              <div className="overflow-x-auto">

                <table className="min-w-full text-sm border">

                  <thead>

                    <tr className="bg-gray-100">

                      <th className="px-4 py-2 border">Date</th>

                      <th className="px-4 py-2 border">Check In</th>

                      <th className="px-4 py-2 border">Check Out</th>

                      <th className="px-4 py-2 border">Status</th>

                      <th className="px-4 py-2 border">Duration</th>

                    </tr>

                  </thead>

                  <tbody>

                    {attendance.length === 0 ? (

                      <tr><td colSpan={5} className="text-center py-4 text-gray-400">No attendance records found.</td></tr>

                    ) : (

                      attendance.sort((a, b) => (a.date < b.date ? 1 : -1)).map((a, i) => {
                        let status = 'Absent';
                        if (a.checkIn && a.checkOut) status = 'Present';
                        else if (a.checkIn && !a.checkOut) status = 'Checked In';
                        
                        // Calculate duration if both check-in and check-out times exist
                        const duration = a.checkIn && a.checkOut ? calculateDuration(a.checkIn, a.checkOut) : '-';
                        
                        return (
                          <tr key={i}>
                            <td className="border px-4 py-2">{a.date}</td>
                            <td className="border px-4 py-2">{a.checkIn || '-'}</td>
                            <td className="border px-4 py-2">{a.checkOut || '-'}</td>
                            <td className="border px-4 py-2">{status}</td>
                            <td className="border px-4 py-2">{duration}</td>
                          </tr>
                        );
                      })

                    )}

                  </tbody>

                </table>

              </div>

            </>

          )}

        </div>

      </div>

    </div>

  );

}



export default StudentDetails; 
